<template>
  <ag-modal-container
    :value="visible"
    :title="$t('animation.multiproject.draw.title')"
    width="600px"
    @close="close"
  >
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="dataTableLoading"
      :loading-text="$t('general.loading')"
      item-class="class"
      class="mb-5"
      disable-pagination
      hide-default-footer
      dense
    >
      <template v-slot:[`item.orderNumber`]="{ item }">
        <div class="font-weight-bold" v-text="item.orderNumber" />
      </template>
      <template v-slot:[`item.blue`]="{ item }">
        <ag-data-table-edit-dialog
          :value="item.blue"
          :rules="$options.rules.colors"
          @input="value[item.orderNumber - 1].blue = $event"
        />
      </template>
      <template v-slot:[`item.green`]="{ item }">
        <ag-data-table-edit-dialog
          :value="item.green"
          :rules="$options.rules.colors"
          @input="value[item.orderNumber - 1].green = $event"
        />
      </template>
      <template v-slot:[`item.yellow`]="{ item }">
        <ag-data-table-edit-dialog
          :value="item.yellow"
          :rules="$options.rules.colors"
          @input="value[item.orderNumber - 1].yellow = $event"
        />
      </template>
      <template v-slot:[`item.red`]="{ item }">
        <ag-data-table-edit-dialog
          :value="item.red"
          :rules="$options.rules.colors"
          @input="value[item.orderNumber - 1].red = $event"
        />
      </template>
    </v-data-table>

    <template v-slot:footer>
      <v-spacer />
      <v-btn
        color="primary"
        class="ml-5"
        outlined
        @click="close"
        v-text="$t('general.cancel')"
      />
      <v-btn
        :loading="updateLoading"
        :disabled="!isValidDraw || !value"
        color="accent"
        class="ml-5"
        @click="update"
        v-text="$t('general.update')"
      />
    </template>
  </ag-modal-container>
</template>

<script>
import { mapActions } from 'vuex'
import Helpers from 'src/utils/helpers'
import FormRules from 'src/utils/form-rules'
import Repositories from 'src/repositories'

import AgModalContainer from 'src/components/common/AgModalContainer'
import AgDataTableEditDialog from 'src/components/common/AgDataTableEditDialog'

export default {
  name: 'DrawContainer',
  components: {
    AgModalContainer,
    AgDataTableEditDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    gameId: {
      type: String,
      required: true,
    },
    currentRound: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    dataTableLoading: false,
    updateLoading: false,
    value: null,
  }),
  computed: {
    headers () {
      return [
        {
          text: this.$t('multiproject.fields.draw.round'),
          value: 'orderNumber',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.blue'),
          value: 'blue',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.green'),
          value: 'green',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.yellow'),
          value: 'yellow',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.red'),
          value: 'red',
          align: 'center',
          sortable: false,
        },
      ]
    },
    isValidDraw () {
      return this.value?.every(this.isValidRound)
    },
    items () {
      return this.value?.map(item => ({
        ...item,
        class: item.orderNumber < this.currentRound ? 'draw-container--passed-round' : '',
      }))
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler () {
        if (this.visible) {
          this.dataTableLoading = true
          this.value = await Repositories.animation.multiprojectDraw.get(this.gameId)
          this.dataTableLoading = false
        }
      },
    },
  },
  methods: {
    ...mapActions('ui', {
      showSuccessNotification: 'showSuccessNotification',
      showErrorNotification: 'showErrorNotification',
    }),
    close () {
      this.$emit('update:visible', false)
    },
    async update () {
      this.updateLoading = true
      try {
        await Repositories.animation.multiprojectDraw.patch(this.gameId, this.value)
        this.showSuccessNotification(this.$t('animation.multiproject.draw.success'))
        this.close()
      }
      catch (error) {
        const notification = Repositories.parseErrorMessage(error)
        this.showErrorNotification(notification)
      }
      finally {
        this.updateLoading = false
      }
    },
    isValidRound ({
      orderNumber, blue, green, yellow, red,
    }) {
      return Helpers.isBetween(orderNumber, { min: 1, max: 100 })
        && Helpers.isBetween(blue, { min: 1, max: 9 })
        && Helpers.isBetween(green, { min: 1, max: 9 })
        && Helpers.isBetween(yellow, { min: 1, max: 9 })
        && Helpers.isBetween(red, { min: 1, max: 9 })
    },
  },
  rules: {
    colors: [
      FormRules.between('multiproject.fields.error.between', { min: 1, max: 9 }),
    ],
  },
}
</script>

<style lang="scss">
.draw-container--passed-round {
  pointer-events: none;
  background-color: #EEE;
  opacity: 0.4;
}
</style>
